import { AddPrimitivesCommand, BasicControl,  CURSOR_TYPE, dymode, DYMODE_MODE, Listeners, MOUSE, Point, TnEngineContext, TnEngineExtraContext, ViewEditor } from "pytha";
import { STRUCTURE_CTRL_TYPE } from "../enums/structure-control-type";
import { Vector3 } from "three";
import { SWallT } from "../models/SWallT";
import { STRUCTURE_SIGNAL } from "../enums/structure-signal";

/**
 * 添加结构墙二级控制器
 * 指定起点和终点布置
 */
export class AddSWallControl extends BasicControl {

    id = STRUCTURE_CTRL_TYPE.ADD_SWALL;

    needInit: boolean;

    capturePoint: Vector3;
    point_down: Point = new Point();
    point_move: Point = new Point();

    // 第一个点坐标
    firstPoint: Point = new Point();
    // 第二个点坐标
    secondPoint: Point = new Point();

    context: TnEngineContext;
    extraContext: TnEngineExtraContext;

    helpWall: SWallT = new SWallT();

    // 厚度
    thickness: number = 0;
    // 高度
    height: number = 0;

    layerUuid: string = '';
    ex: number = 0;
    ez: number = 0;

    step: number = 1;
    private boundInitControl: Function;
    // 构件信息
    componentData: any;

    constructor(editor: ViewEditor, listeners: Listeners) {
        super();
        this.needInit = true;
        this.context = TnEngineContext.getInstance();
        this.extraContext = TnEngineExtraContext.getInstance();

        this.editor = editor;
        this.listeners = listeners;

        this.editor.clampControl.enabled = false;
        this.editor.clampControl.hidden = true;

        this.boundInitControl = this.initControl.bind(this);
        this.listeners.getSignal(STRUCTURE_SIGNAL.initSWallControl).add(this.boundInitControl);

        this.listeners.signals.onPointCapture.add(this.onPointCapture)
        this.editor.renderer.domElement.addEventListener('pointerdown', this.onPointerDown);
        this.editor.renderer.domElement.addEventListener('pointermove', this.onPointerMove);

        console.log('AddSWallControl add')
    }

    dispose(): void {
        this.listeners.getSignal(STRUCTURE_SIGNAL.initSWallControl).remove(this.boundInitControl);

        this.listeners.signals.onPointCapture.remove(this.onPointCapture);
        this.listeners.signals.noNeedCapture.dispatch();
        this.editor.renderer.domElement.removeEventListener('pointerdown', this.onPointerDown);
        this.editor.renderer.domElement.removeEventListener('pointermove', this.onPointerMove);


        this.editor.selectControl.setEnabled(true);
        this.listeners.signals.onDymodeChanged.remove(this.onDymodeChanged);
        this.editor.clampControl.enabled = true;
        this.editor.clampControl.hidden = false;

        if (this.helpWall && this.helpWall.viewObj && this.helpWall.viewObj.parent) {
            this.helpWall.viewObj.parent?.remove(this.helpWall.viewObj);
        }

        console.log('AddSWallControl removed')
    }

    onPointCapture = (point) => {
        this.capturePoint = point;
    }

    onDymodeChanged = (value: dymode) => {
        if (value.mode === DYMODE_MODE.POINT) {
            if (this.step == 1) {
                // 获得了第一个点
                this.firstPoint.set(value.x, value.y, value.z);
                this.firstStep();
            } else if (this.step == 2) {
                // 获得了第二个点
                this.secondPoint.set(value.x, value.y, value.z);
                this.secondStep();
            }
        } else if (value.mode === DYMODE_MODE.DIST) {
            if (this.step == 2) {
                this._dir = this.point_move.clone().sub(this.firstPoint).normalize();
                let end = this._dir.multiplyScalar(value.dist).add(this.firstPoint);
                this.secondPoint.set(end.x, end.y, end.z);
                this.secondStep();
            }
        }
    }

    onPointerDown = (event) => {
        if (event.button === MOUSE.RIGHT) {
            this.extraContext.listeners.signals.onOpeCommandControlEnd.dispatch();
        }
        if (event.button !== MOUSE.LEFT) return;
        let rect = this.editor.renderer.domElement.getBoundingClientRect();
        let x = (event.clientX - rect.x) / rect.width * 2 - 1;
        let y = -(event.clientY - rect.y) / rect.height * 2 + 1;
        this.context.ucsContext.unprojectCursorPoint(x, y, this.editor.camera, this.point_down, this.context.workPlaneContext.currentWorkPlane?.ucs.workPlane);
        if (this.capturePoint) {
            this.point_down.copy(this.capturePoint);
        }

        if (this.step == 1) {
            // 获得了第一个点坐标
            this.firstPoint = this.point_down.clone();
            this.firstStep();
        } else if (this.step == 2) {
            // 获得了第二个点坐标
            this.secondPoint = this.point_down.clone();
            this.secondStep();
        }
    }

    initControl = (data: any): void => {
        this.componentData = data;
        this.onAssembleSWall(data);
    }

    onAssembleSWall = (data: any) => {
        let initData = { ...data };

        this.ex = data.ex;
        this.ez = data.ez;
        this.thickness = data.thickness ? data.thickness : 0;
        this.height = data.height;
        this.layerUuid = data.layerUuid;

        this.listeners.signals.setInitControlData.dispatch(initData);

        if (this.helpWall && this.helpWall.viewObj && this.helpWall.viewObj.parent) {
            this.helpWall.viewObj.parent?.remove(this.helpWall.viewObj);
        }

        this.listeners.signals.needCapture.dispatch();

        this.editor.selectControl.setEnabled(false);
        this.listeners.signals.sendToDymodeTip.dispatch('选择第1个节点:');
        this.extraContext.stmodeContext.setStmode('选择第1个节点:');
        this.step = 1;
        this.editor.renderer.domElement.focus();
        this.listeners.signals.onCursorChange.dispatch(CURSOR_TYPE.CROSS);
    }

    firstStep = () => {
        this._xDir = this.context.workPlaneContext.currentWorkPlane?.ucs.axisX.clone() ?
            this.context.workPlaneContext.currentWorkPlane?.ucs.axisX.clone() : new Vector3(1, 0, 0);
        this._yDir = this.context.workPlaneContext.currentWorkPlane?.ucs.axisY.clone() ?
            this.context.workPlaneContext.currentWorkPlane?.ucs.axisY.clone() : new Vector3(0, 1, 0);
        this._zDir = this.context.workPlaneContext.currentWorkPlane?.ucs.axisZ.clone() ?
            this.context.workPlaneContext.currentWorkPlane?.ucs.axisZ.clone() : new Vector3(0, 0, 1);

        this.helpWall._geo.set(this.point_down.clone().move(this._xDir, -this.thickness / 2),
            this.thickness, 0, this.height, this._xDir, this._yDir, this._zDir);
        this.extraContext.sceneContext.scene.add(this.helpWall.render());
        this.editor.listeners.signals.needRender.dispatch();
        this.step++;

        this.listeners.signals.sendToDymodeTip.dispatch('选择第2个节点:');
        this.extraContext.stmodeContext.setStmode('选择第2个节点:');
    }

    secondStep = () => {
        this._yDir.subVectors(this.point_down, this.firstPoint).normalize();
        this._xDir.crossVectors(this._yDir, this._zDir);
        this.helpWall._geo.set(this.firstPoint.clone().move(this._xDir, -this.thickness / 2).move(this._xDir, this.ex).move(this._zDir, this.ez), this.thickness, this.point_move.distanceTo(this.firstPoint), this.height, this._xDir, this._yDir, this._zDir);
        let wall = this.helpWall.clone();
        wall.layerUuid = this.layerUuid;
        wall.isColorByLayer = true;
        wall.materialUuid = this.componentData.materialUuid;

        this.editor.history.execute(new AddPrimitivesCommand([wall]));
        this.onAssembleSWall(this.componentData);
    }

    private _dir = new Vector3();
    private _xDir = new Vector3();
    private _yDir = new Vector3();
    private _zDir = new Vector3();
    onPointerMove = (event) => {
        if (this.step != 2) return;
        // 获取鼠标在当前工作平面上的点
        let rect = this.editor.renderer.domElement.getBoundingClientRect();
        let x = (event.clientX - rect.x) / rect.width * 2 - 1;
        let y = -(event.clientY - rect.y) / rect.height * 2 + 1;

        if (this.capturePoint) {
            this.point_move.copy(this.capturePoint);
        } else {
            this.context.ucsContext.unprojectCursorPoint(x, y, this.editor.camera, this.point_move,
                this.context.workPlaneContext.currentWorkPlane?.ucs.workPlane);
        }
        this._yDir.subVectors(this.point_move, this.firstPoint).normalize();
        this._xDir.crossVectors(this._yDir, this._zDir);

        this.helpWall._geo.set(this.firstPoint.clone().move(this._xDir, -this.thickness / 2),
            this.thickness, this.point_move.distanceTo(this.firstPoint), this.height, this._xDir, this._yDir, this._zDir);
        this.helpWall.updateViewGeometry();
        this.editor.listeners.signals.needRender.dispatch();
    }
}